import { ClockIcon } from '../../components/icons.js';
import { useEffect, useState } from 'react';
import { classifyScheduleStatus, scheduleFailureReason } from '@canopy/shared';
import {
  useCreateReportSchedule,
  useCreateSchedule,
  useDeleteSchedule,
  useReportSchedules,
  useRunScheduleNow,
  useScheduleDeliveries,
  useTemplateSchedules,
  useToggleSchedule,
  type DeliveryChannel,
  type DeliveryConfig,
  type Schedule,
  type ScheduleFormat,
} from '../../api/schedules.js';
import { ScheduleFields, cronToHuman } from '../../components/schedule-fields.js';
import { SCOPE_UNRESOLVED_MESSAGE, describeWriteFailure } from './report-view-state.js';
import { getActiveScopePath } from '../../auth/active-scope.js';

/**
 * SchedulePanel — manage scheduled deliveries for a template or, when
 * `reportId` is supplied, for a specific report INSTANCE.
 *
 * The distinction matters (CSAGE-353). `GET /schedules`, which backs the admin
 * Subscriptions page, returns only `artifact_type = 'report'` rows for a scoped
 * session; report-template schedules carry no `scope_path` and are excluded
 * fail-closed so one customer's subscriptions can never leak into another's list
 * (schedule.service.ts listForTenant). A subscription created against a TEMPLATE
 * therefore disappears from the admin page while still showing on the report
 * that created it — reported from UAT, and the same reason the consumer
 * Subscriptions button looked empty for reports that plainly had subscriptions.
 *
 * So: consumer surfaces pass `reportId` and operate on the instance. The
 * template mode remains for Sage authoring, where a template-level schedule is
 * a deliberate tenant-wide artefact.
 *
 * Both hook pairs are called unconditionally — hooks cannot be conditional — and
 * the unused one is passed `null`, which its `enabled` guard turns into a no-op.
 */
export function SchedulePanel({
  templateId,
  reportId,
  params,
  onEditingChange,
}: {
  templateId: string;
  reportId?: string;
  /**
   * CSAGE-384: parameter values a NEW subscription inherits — the ones on
   * screen in the report the user opened this from. Omitted by the authoring
   * surface (TemplateDetail), which has no live render to inherit from.
   */
  params?: Record<string, unknown>;
  onEditingChange?: (editing: boolean) => void;
}): JSX.Element {
  const templateSchedules = useTemplateSchedules(reportId ? null : templateId);
  const reportSchedules = useReportSchedules(reportId ?? null);
  const schedules = reportId ? reportSchedules : templateSchedules;
  const [showNew, setShowNew] = useState(false);
  useEffect(() => {
    onEditingChange?.(showNew);
  }, [showNew, onEditingChange]);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="card-body stack-lg subscription-overview">
      {schedules.data && schedules.data.length > 0 && (
        <div className="section-head">
          <div className="section-head-title">
            <h2>
              Delivery schedules <span className="subscription-total">{schedules.data.length}</span>
            </h2>
            <p>Review delivery schedules, pause a subscription, or check its recent activity.</p>
          </div>
          <button className="btn btn--primary" type="button" onClick={() => setShowNew(true)}>
            + New subscription
          </button>
        </div>
      )}
      {schedules.isLoading && (
        <div className="text-dim" role="status">
          Loading subscriptions…
        </div>
      )}
      {schedules.isError && (
        <div className="alert alert--danger" role="alert">
          <p>We couldn’t load subscriptions. Please try again.</p>
          <button
            className="btn btn--secondary"
            type="button"
            onClick={() => void schedules.refetch()}
          >
            Try again
          </button>
        </div>
      )}
      {!schedules.isError && schedules.data?.length === 0 && (
        <div className="subscription-welcome">
          <span className="subscription-welcome-icon" aria-hidden="true">
            <ClockIcon />
          </span>
          <h2>Get this report delivered</h2>
          <p>
            There are no subscriptions for this report yet. Create one to receive it automatically,
            or arrange a one-time delivery.
          </p>
          <div className="subscription-benefits">
            <div>
              <span>01</span>
              <h3>Choose when</h3>
              <p>Daily, weekly, monthly, or just once.</p>
            </div>
            <div>
              <span>02</span>
              <h3>Choose where</h3>
              <p>Email recipients, a webhook, or an SFTP destination.</p>
            </div>
            <div>
              <span>03</span>
              <h3>Choose a format</h3>
              <p>PDF, Excel, CSV, HTML, or JSON.</p>
            </div>
          </div>
          <button className="btn btn--primary" type="button" onClick={() => setShowNew(true)}>
            Create subscription
          </button>
          <p className="subscription-context-note">
            Your current report parameters will carry over. You can review them before creating the
            subscription.
          </p>
        </div>
      )}
      {schedules.data && schedules.data.length > 0 && (
        <>
          <div className="subscription-status-summary" aria-label="Subscription summary">
            <div>
              <span className="subscription-summary-dot subscription-summary-dot--enabled" />
              <span>Enabled</span>
              <strong>{schedules.data.filter((s) => s.enabled).length}</strong>
            </div>
            <div>
              <span className="subscription-summary-dot" />
              <span>Paused</span>
              <strong>{schedules.data.filter((s) => !s.enabled).length}</strong>
            </div>
            <div>
              <span className="subscription-summary-dot subscription-summary-dot--attention" />
              <span>Needs attention</span>
              <strong>
                {
                  schedules.data.filter(
                    (s) =>
                      s.enabled &&
                      ['failed', 'partial'].includes(classifyScheduleStatus(s.lastStatus)),
                  ).length
                }
              </strong>
            </div>
          </div>
          <div className="card subscription-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Schedule</th>
                  <th>Next run</th>
                  <th>Last run</th>
                  <th>Status</th>
                  <th style={{ width: 310, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.data.map((s) => (
                  <ScheduleRow
                    key={s.id}
                    schedule={s}
                    expanded={expanded === s.id}
                    onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p className="subscription-list-note">
            Schedules use the timezone shown. Run timestamps use your browser’s timezone.
          </p>
        </>
      )}

      {showNew && (
        <NewScheduleDialog
          templateId={templateId}
          {...(reportId ? { reportId } : {})}
          params={params ?? {}}
          onClose={() => setShowNew(false)}
        />
      )}
    </div>
  );
}

function ScheduleRow({
  schedule,
  expanded,
  onToggle,
}: {
  schedule: Schedule;
  expanded: boolean;
  onToggle(): void;
}): JSX.Element {
  const run = useRunScheduleNow();
  const del = useDeleteSchedule();
  const toggle = useToggleSchedule();
  const deliveries = useScheduleDeliveries(expanded ? schedule.id : null);

  return (
    <>
      <tr className="subscription-data-row">
        <td>
          <div className="col" style={{ gap: 2 }}>
            <strong>{schedule.name}</strong>
            <span className="text-mute text-xs">
              {schedule.delivery.format.toUpperCase()} ·{' '}
              {schedule.delivery.channels.map((c) => c.kind).join(', ')}
            </span>
            <span className="subscription-destination">
              {schedule.delivery.channels
                .map((channel) =>
                  channel.kind === 'email'
                    ? channel.recipients.join(', ')
                    : channel.kind === 'sftp'
                      ? channel.host
                      : channel.url,
                )
                .join(' · ')}
            </span>
          </div>
        </td>
        <td className="text-sm">
          <span>{cronToHuman(schedule.cron)}</span>
          <span className="subscription-timezone">{schedule.tz}</span>
        </td>
        <td className="text-sm text-dim">
          {schedule.enabled ? (
            schedule.nextRunAt ? (
              formatRelFuture(schedule.nextRunAt)
            ) : (
              '—'
            )
          ) : (
            <span className="text-mute">paused</span>
          )}
        </td>
        <td className="text-sm text-dim">
          {schedule.lastRunAt ? (
            formatRelPast(schedule.lastRunAt)
          ) : (
            <span className="text-mute">never</span>
          )}
        </td>
        <td>
          <StatusBadge status={schedule.lastStatus} enabled={schedule.enabled} />
        </td>
        <td style={{ textAlign: 'right' }}>
          <div
            className="row subscription-row-actions"
            style={{ gap: 6, justifyContent: 'flex-end' }}
          >
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={run.isPending}
              onClick={() => run.mutate(schedule.id)}
            >
              {run.isPending ? '…' : 'Run now'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={toggle.isPending}
              onClick={() => toggle.mutate({ id: schedule.id, enabled: !schedule.enabled })}
            >
              {schedule.enabled ? 'Pause' : 'Resume'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onToggle}>
              {expanded ? 'Hide history' : 'History'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={del.isPending}
              aria-label={`Delete subscription ${schedule.name}`}
              title="Delete subscription"
              onClick={() => {
                if (confirm(`Delete subscription "${schedule.name}"?`)) del.mutate(schedule.id);
              }}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--color-surface-1)' }}>
            <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
              <div
                className="text-mute text-xs"
                style={{
                  marginBottom: 6,
                  letterSpacing: 'var(--letter-wide)',
                  textTransform: 'uppercase',
                }}
              >
                Delivery history
              </div>
              {deliveries.isLoading && <div className="text-dim text-sm">Loading…</div>}
              {deliveries.data && deliveries.data.length === 0 && (
                <div className="text-mute text-sm">No deliveries yet.</div>
              )}
              {deliveries.data && deliveries.data.length > 0 && (
                <table className="data-table" style={{ background: 'transparent' }}>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Channel</th>
                      <th>Status</th>
                      <th>Attempts</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.data.map((d) => (
                      <tr key={d.id}>
                        <td className="text-sm text-dim">
                          {d.deliveredAt
                            ? formatRelPast(d.deliveredAt)
                            : formatRelPast(d.createdAt)}
                        </td>
                        <td>
                          <code>{d.channel}</code>
                        </td>
                        <td>
                          <DeliveryStatusBadge status={d.status} />
                        </td>
                        <td className="cell-align-right">{d.attempts}</td>
                        <td className="text-sm">
                          {d.lastError ? (
                            <span className="text-mute text-xs">{d.lastError.slice(0, 100)}</span>
                          ) : d.runId ? (
                            <span className="cell-mono">run {d.runId.slice(0, 8)}…</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatusBadge({
  status,
  enabled,
}: {
  status: string | null;
  enabled: boolean;
}): JSX.Element {
  // CSAGE-346: this component already had the vocabulary right; it now
  // classifies via @canopy/shared so it can't drift from the runner.
  if (!enabled) return <span className="badge badge--neutral">Paused</span>;
  const state = classifyScheduleStatus(status);
  if (state === 'never') return <span className="badge badge--neutral">Idle</span>;
  if (state === 'running')
    return (
      <span className="badge badge--info">
        <span className="badge-dot" /> Running
      </span>
    );
  if (state === 'succeeded')
    return (
      <span className="badge badge--success">
        <span className="badge-dot" /> Healthy
      </span>
    );
  if (state === 'partial') return <span className="badge badge--warning">Partial</span>;
  return (
    <span className="badge badge--danger" title={scheduleFailureReason(status) ?? status ?? ''}>
      Failed
    </span>
  );
}

function DeliveryStatusBadge({ status }: { status: string }): JSX.Element {
  if (status === 'sent') return <span className="badge badge--success">sent</span>;
  if (status === 'pending') return <span className="badge badge--info">pending</span>;
  if (status === 'failed') return <span className="badge badge--warning">failed</span>;
  if (status === 'dead') return <span className="badge badge--danger">dead</span>;
  return <span className="badge badge--neutral">{status}</span>;
}

function formatRelPast(iso: string): string {
  const d = new Date(iso);
  const age = Math.round((Date.now() - d.getTime()) / 1000);
  if (age < 60) return `${age}s ago`;
  if (age < 3600) return `${Math.round(age / 60)}m ago`;
  if (age < 86400) return `${Math.round(age / 3600)}h ago`;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelFuture(iso: string): string {
  const d = new Date(iso);
  const dt = Math.round((d.getTime() - Date.now()) / 1000);
  if (dt < 0) return 'due now';
  if (dt < 60) return `in ${dt}s`;
  if (dt < 3600) return `in ${Math.round(dt / 60)}m`;
  if (dt < 86400) return `in ${Math.round(dt / 3600)}h`;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── New subscription dialog (CSAGE-266/267/268) ───────────────────────────

type ChannelKind = 'email' | 'webhook' | 'sftp';
type Timing = 'schedule' | 'once';

/**
 * CSAGE-384: build a new subscription's delivery config.
 *
 * Exists to make `params` a REQUIRED argument. The bug this closes was a
 * literal `params: {}` inline at the call site — the subscription inherited
 * nothing from the report the user was looking at, and because the runner
 * merges `{...report.default_params, ...delivery.params}`, an empty object
 * silently handed the render to the report's own defaults. For a report
 * provisioned at an ancestor node that is the ancestor's entire subtree,
 * delivered weekly, with nothing on screen to suggest it.
 *
 * A required parameter cannot be forgotten the way an inline literal can, and
 * an empty selection now has to be passed deliberately.
 */
export function buildSubscriptionDelivery(
  format: ScheduleFormat,
  params: Record<string, unknown>,
  channel: DeliveryChannel,
): DeliveryConfig {
  return { format, params, channels: [channel] };
}

/**
 * The parameters a subscription will carry, as label/value pairs for display.
 *
 * Shown in the dialog because the failure mode being fixed was SILENT: the user
 * had no way to tell that the narrowing they had applied on screen was about to
 * be dropped. Sorted for a stable read; values stringified defensively so an
 * unexpected shape renders rather than throwing inside a dialog.
 */
export function describeInheritedParams(
  params: Record<string, unknown>,
): ReadonlyArray<{ name: string; value: string }> {
  return Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    .map(([name, v]) => ({ name, value: String(v) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function NewScheduleDialog({
  templateId,
  reportId,
  params,
  onClose,
}: {
  templateId: string;
  reportId?: string;
  /** CSAGE-384: on-screen parameters this subscription inherits. */
  params: Record<string, unknown>;
  onClose(): void;
}): JSX.Element {
  // Create against the instance when we have one, so the subscription is
  // scope-tagged and therefore visible in the admin Subscriptions page. See the
  // SchedulePanel docblock for why a template-level schedule is invisible there.
  const createForTemplate = useCreateSchedule(reportId ? null : templateId);
  const createForReport = useCreateReportSchedule(reportId ?? null);
  const create = reportId ? createForReport : createForTemplate;
  const runNow = useRunScheduleNow();
  const rawError = create.error ?? runNow.error;
  const failure = rawError ? describeWriteFailure(rawError) : null;
  /**
   * CSAGE-384: the server refuses a subscription whose data scope it cannot
   * determine, and `x-canopy-scope` — sourced from this exact value — is the
   * only thing that tells it. Checking here turns a rejection that arrives AFTER
   * the user has typed a name, a cadence, a timezone and four recipients into a
   * warning they see before starting. Same condition as the server's guard, so
   * the two cannot disagree about whether this submit can succeed.
   *
   * Deliberately not a silent disable: an unexplained greyed-out button is worse
   * than a stated reason. The submit stays clickable and the server remains the
   * authority — this only front-runs its answer.
   */
  const scopeMissing = getActiveScopePath() === null;
  const inherited = describeInheritedParams(params);

  const [name, setName] = useState('');
  const [timing, setTiming] = useState<Timing>('schedule');
  const [alsoNow, setAlsoNow] = useState(false);
  const [cron, setCron] = useState('0 8 * * 1');
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [cronValid, setCronValid] = useState(true);
  const [format, setFormat] = useState<ScheduleFormat>('pdf');

  const [channelKind, setChannelKind] = useState<ChannelKind>('email');
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  // SFTP (CSAGE-268)
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('');
  const [remotePath, setRemotePath] = useState('');
  const [secretRef, setSecretRef] = useState('');
  const [filenamePattern, setFilenamePattern] = useState('');

  // CSAGE-313: the server now rejects a non-`https://` webhook URL with a
  // 400 (SAGE_WEBHOOK_ALLOWED_HOSTS is the operator-only escape hatch for
  // a genuine internal http:// endpoint) — require it here too, so the
  // author gets inline feedback instead of a round-trip.
  const webhookUrlValid = /^https:\/\//.test(webhookUrl.trim());

  const channelValid =
    channelKind === 'email'
      ? recipients.trim().length > 0
      : channelKind === 'webhook'
        ? webhookUrlValid
        : host.trim().length > 0 &&
          username.trim().length > 0 &&
          remotePath.trim().length > 0 &&
          secretRef.trim().length > 0;

  const canSubmit = name.trim().length > 0 && (timing === 'once' || cronValid) && channelValid;

  function buildChannel(): DeliveryChannel {
    if (channelKind === 'email') {
      const recs = recipients
        .split(/[,\n\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      return {
        kind: 'email',
        recipients: recs,
        ...(subject.trim() ? { subject: subject.trim() } : {}),
      };
    }
    if (channelKind === 'webhook') return { kind: 'webhook', url: webhookUrl.trim() };
    return {
      kind: 'sftp',
      host: host.trim(),
      port: Number(port) || 22,
      username: username.trim(),
      path: remotePath.trim(),
      secretRef: secretRef.trim(),
      ...(filenamePattern.trim() ? { filenamePattern: filenamePattern.trim() } : {}),
    };
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const once = timing === 'once';
    // CSAGE-387: `try` so a rejected mutation doesn't escape as an unhandled
    // promise rejection out of the form's onSubmit. Swallowing is correct here
    // and only here: react-query has already recorded the failure on
    // `create.error` / `runNow.error`, which is what the banner above renders.
    // Bailing before `onClose()` is what keeps the dialog — and every value the
    // user typed into it — alive for the retry.
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        // A one-time delivery is stored as a paused schedule (won't recur) that
        // we fire immediately; a recurring one uses the builder's cron.
        cron: once ? '0 8 * * 1' : cron,
        tz,
        enabled: !once,
        delivery: buildSubscriptionDelivery(format, params, buildChannel()),
      });
      if ((once || alsoNow) && created?.id) {
        await runNow.mutateAsync(created.id);
      }
    } catch {
      return;
    }
    onClose();
  }

  return (
    <div className="modal-backdrop">
      <div
        className="modal subscription-editor-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-subscription-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <h2 id="new-subscription-title">New subscription</h2>
            <div className="modal-subtitle">Deliver this report on a schedule or on demand.</div>
          </div>
          <button className="btn-icon" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="modal-body subscription-form-grid">
            <section className="subscription-form-section">
              <h3>Schedule & timing</h3>
              <p className="subscription-section-help">
                Give this delivery a name and choose when it runs.
              </p>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Weekly Device Health"
                  autoFocus
                />
              </label>

              {/* Timing: recurring schedule vs one-time now (CSAGE-267) */}
              <div className="field">
                <span className="field-label">Delivery</span>
                <div
                  className="row"
                  style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
                >
                  <button
                    type="button"
                    className={'chip' + (timing === 'schedule' ? ' chip--on' : '')}
                    onClick={() => setTiming('schedule')}
                  >
                    On a schedule
                  </button>
                  <button
                    type="button"
                    className={'chip' + (timing === 'once' ? ' chip--on' : '')}
                    onClick={() => setTiming('once')}
                  >
                    Once, now
                  </button>
                </div>
                {timing === 'schedule' ? (
                  <>
                    <ScheduleFields
                      cron={cron}
                      tz={tz}
                      onChange={(v) => {
                        setCron(v.cron);
                        setTz(v.tz);
                      }}
                      onValidityChange={setCronValid}
                    />
                    <label className="row" style={{ gap: 8, marginTop: 'var(--space-2)' }}>
                      <input
                        type="checkbox"
                        checked={alsoNow}
                        onChange={(e) => setAlsoNow(e.target.checked)}
                      />
                      <span className="text-sm">Also send one delivery now</span>
                    </label>
                  </>
                ) : (
                  <p className="text-dim text-sm" style={{ margin: 0 }}>
                    Delivers once, immediately. It won't repeat (kept paused so you can see the
                    delivery result).
                  </p>
                )}
              </div>
            </section>
            <section className="subscription-form-section">
              <h3>Content & destination</h3>
              <p className="subscription-section-help">
                Choose the file format and where to send it.
              </p>
              <label className="field">
                <span className="field-label">Format</span>
                <select
                  className="select"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as ScheduleFormat)}
                >
                  <option value="pdf">PDF</option>
                  <option value="xlsx">Excel (XLSX)</option>
                  <option value="csv">CSV</option>
                  <option value="html">HTML</option>
                  <option value="json">JSON (data only)</option>
                </select>
              </label>

              <div className="field">
                <span className="field-label">Deliver to</span>
                <div
                  className="row"
                  style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
                >
                  {(['email', 'webhook', 'sftp'] as ChannelKind[]).map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={'chip' + (channelKind === k ? ' chip--on' : '')}
                      onClick={() => setChannelKind(k)}
                    >
                      {k === 'email' ? 'Email' : k === 'webhook' ? 'Webhook' : 'SFTP'}
                    </button>
                  ))}
                </div>

                {channelKind === 'email' && (
                  <>
                    <label className="field">
                      <span className="field-label">Email recipients</span>
                      <textarea
                        className="textarea"
                        value={recipients}
                        onChange={(e) => setRecipients(e.target.value)}
                        placeholder="you@company.com, ops@company.com"
                        rows={3}
                        style={{ fontFamily: 'inherit' }}
                      />
                      <span className="field-help">Separate email addresses with commas.</span>
                    </label>
                    <label className="field">
                      <span className="field-label">Email subject (optional)</span>
                      <input
                        className="input"
                        style={{ marginTop: 6 }}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Your report is ready"
                      />
                    </label>
                  </>
                )}

                {channelKind === 'webhook' && (
                  <>
                    <input
                      aria-label="Webhook URL"
                      className="input input--mono"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://example.com/webhook"
                    />
                    <span
                      className="field-help"
                      style={
                        webhookUrl.trim() && !webhookUrlValid
                          ? { color: 'var(--color-danger)' }
                          : undefined
                      }
                    >
                      {webhookUrl.trim() && !webhookUrlValid
                        ? 'Webhook URL must start with https://. Ask an operator to add this host to SAGE_WEBHOOK_ALLOWED_HOSTS if it needs http://.'
                        : 'Must be https://. Internal http:// destinations require an operator-configured allow-list entry.'}
                    </span>
                  </>
                )}

                {channelKind === 'sftp' && (
                  <div className="grid-2">
                    <label className="field" style={{ margin: 0, gridColumn: '1 / -1' }}>
                      <span className="field-label">Host</span>
                      <input
                        className="input"
                        value={host}
                        onChange={(e) => setHost(e.target.value)}
                        placeholder="sftp.customer.com"
                      />
                    </label>
                    <label className="field" style={{ margin: 0 }}>
                      <span className="field-label">Port</span>
                      <input
                        className="input"
                        type="number"
                        value={port}
                        onChange={(e) => setPort(e.target.value)}
                        placeholder="22"
                      />
                    </label>
                    <label className="field" style={{ margin: 0 }}>
                      <span className="field-label">Username</span>
                      <input
                        className="input"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="canopy"
                      />
                    </label>
                    <label className="field" style={{ margin: 0, gridColumn: '1 / -1' }}>
                      <span className="field-label">Remote path</span>
                      <input
                        className="input"
                        value={remotePath}
                        onChange={(e) => setRemotePath(e.target.value)}
                        placeholder="inbound"
                      />
                    </label>
                    <label className="field" style={{ margin: 0 }}>
                      <span className="field-label">Credential</span>
                      <input
                        className="input"
                        value={secretRef}
                        onChange={(e) => setSecretRef(e.target.value)}
                        placeholder="named SSH credential"
                      />
                    </label>
                    <label className="field" style={{ margin: 0 }}>
                      <span className="field-label">Filename date (optional)</span>
                      <input
                        className="input input--mono"
                        value={filenamePattern}
                        onChange={(e) => setFilenamePattern(e.target.value)}
                        placeholder="yyyyMMdd"
                      />
                    </label>
                    <div className="field-help" style={{ gridColumn: '1 / -1' }}>
                      The credential names a securely-stored SSH key (never the key itself). Manage
                      credentials in Admin.
                    </div>
                  </div>
                )}
              </div>
            </section>
            {/* CSAGE-387: was `<pre>{JSON.stringify(err.body)}</pre>`, which showed
                a customer the raw platform envelope and nothing about what to do
                (content-style.md §7). `role="alert"` per accessibility.md §9.
                The dialog stays mounted on failure, so every field the user
                already typed survives the retry — see `submit`. */}
            {/* CSAGE-384: state what the subscription will actually deliver.
                The defect this replaces was silent — the user's on-screen
                narrowing was dropped with nothing to indicate it. */}
            {reportId && (
              <div className="field subscription-parameter-summary">
                <span className="field-label">Report parameters</span>
                {inherited.length > 0 ? (
                  <div className="field-help">
                    {inherited.map((p) => (
                      <div key={p.name}>
                        {p.name}: <strong>{p.value}</strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="field-help">
                    This report&rsquo;s own saved defaults. To deliver a narrower slice, set the
                    parameters on the report first, then subscribe.
                  </div>
                )}
              </div>
            )}

            {/* CSAGE-384: warn before the form is filled, not after it is lost. */}
            {scopeMissing && !failure && (
              <div className="alert alert--warning" role="status">
                {SCOPE_UNRESOLVED_MESSAGE}
              </div>
            )}

            {failure && (
              <div className="alert alert--danger" role="alert">
                <div>{failure.message}</div>
                {/* Keep the code visible for support, but as a detail line
                    rather than the whole message — same shape as the viewer's
                    error banner (ReportViewer.tsx:113). Suppressed when the
                    copy above already says everything actionable. */}
                {!failure.sessionExpired && failure.code && (
                  <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                    {failure.code}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit || create.isPending || runNow.isPending}
            >
              {create.isPending || runNow.isPending
                ? 'Saving…'
                : timing === 'once'
                  ? 'Send now'
                  : 'Create subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
